import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Mail, FileText, BarChart3, MessageSquareQuote,
  CheckCircle2, ArrowRight, Sparkles, Clock, Heart, Quote, LayoutDashboard, Info
} from "lucide-react";

// Anne Flinn Advertising — Suite landing page
// Public route at /suite — the customer acquisition hub for ALL her tools
//
// Brand assets (in /public/brand/):
//   - afa-logo-pin.png   : standalone pin icon
//   - afa-logo-full.png  : pin + "Anne Flinn Advertising · We Find People"
//   - anne-headshot.jpg  : her headshot

const TOOLS = [
  {
    key: "email",
    name: "Simply Smarter Email",
    tagline: "AI-powered newsletters",
    description: "You've already done the hard work. Your website is full of great content. This tool turns any page on your site into a beautiful email in minutes. Send to new customers, stay top-of-mind with existing ones. Already using Simply Smarter Blog Posts? Each post you write becomes an email in one click.",
    icon: Mail,
    color: "from-[#0d9488] to-[#0a1e3a]",
    accent: "bg-[#B7EB1B]",
    status: "live",
    statusLabel: "Available Now",
    price: "$79/mo",
    cta: "Learn More",
    href: "/simply-smarter-email",
    replaces: "Mailchimp · Constant Contact · $300/mo agency",
    features: ["Turns your URLs into emails", "AI drafts the copy & layout", "You tweak colors & photos", "Open & click tracking"],
    note: "Works best with standard blog and info pages with photographs.",
  },
  {
    key: "blog",
    name: "Simply Smarter Blog Posts",
    tagline: "AI blog post writer",
    description: "Answer a few questions about your business. AI writes thoughtful, SEO-friendly blog posts in your voice. Publish or copy with one click. Even better, pair it with Simply Smarter Email and every blog you write doubles as a newsletter to your list. The two tools were built to work together.",
    icon: FileText,
    color: "from-[#119AB3] to-[#003858]",
    accent: "bg-[#B7EB1B]",
    status: "live",
    statusLabel: "Available Now",
    price: "$49/mo",
    cta: "Try It",
    href: "https://blog-factory-6.preview.emergentagent.com/",
    external: true,
    replaces: "Freelance writer · $200/post",
    features: ["15+ industries", "5+ tones", "Series generation", "Download or publish"]
  },
  {
    key: "scorecard",
    name: "Simply Smarter Scorecard",
    tagline: "Brand consistency audit",
    description: "Is your brand consistent across the web? We scan 8 platforms — Facebook, Instagram, TikTok, YouTube, LinkedIn, Google Business, Google Maps, Apple Maps — and grade your presence. See what's working and what needs attention, in 2 minutes.",
    icon: BarChart3,
    color: "from-[#0a1e3a] to-[#003858]",
    accent: "bg-[#B7EB1B]",
    status: "live",
    statusLabel: "Available Now",
    price: "Free",
    cta: "Run Free Audit",
    href: "https://brand-report-r-1773343747.emergent.host/",
    external: true,
    replaces: "SEO audit · $500-1,000",
    features: ["8 platforms scanned", "2-minute analysis", "No signup required", "Prioritized fixes"]
  },
  {
    key: "questionnaire",
    name: "Simply Smarter Customer Questionnaire",
    tagline: "First-party audience data",
    description: "Find out where your customers actually spend their media time. Drop-in survey for any business — streaming, social, podcasts, news. In 5 minutes, you'll know exactly where to buy ads. No more guessing.",
    icon: MessageSquareQuote,
    color: "from-[#006080] to-[#0d9488]",
    accent: "bg-[#B7EB1B]",
    status: "live",
    statusLabel: "Available Now",
    price: "Free",
    cta: "Open Dashboard",
    href: "https://consumer-check-1.preview.emergentagent.com/",
    external: true,
    replaces: "Market research consultant · $1-2k",
    features: ["Embed on your site", "CSV export", "Print-friendly", "Unlimited responses"]
  },
];

const STATUS_STYLES = {
  live: "bg-[#B7EB1B] text-[#0a1e3a]",
  soon: "bg-amber-300 text-amber-900",
  building: "bg-cyan-200 text-cyan-900",
  vision: "bg-white/10 text-white/70 border border-white/20",
};

export default function SuitePage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [email, setEmail] = useState("");
  const [interest, setInterest] = useState([]);
  const [submitted, setSubmitted] = useState(false);

  const toggleInterest = (key) => {
    setInterest((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };

  const handleWaitlist = (e) => {
    e.preventDefault();
    // For now this is a local-only "thanks for joining" — when Stripe is wired,
    // this will POST to /api/waitlist and email Anne with the lead
    if (!email) return;
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#003858] via-[#0a1e3a] to-[#003858] text-white" data-testid="suite-page">
      {/* Sticky nav */}
      <nav className="sticky top-0 z-50 bg-[#003858]/95 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <a href="/suite" className="flex items-center gap-3" data-testid="suite-nav-logo">
            <img src="/brand/afa-logo-pin.png" alt="Anne Flinn Advertising" className="w-10 h-10 object-contain" />
            <div className="flex flex-col leading-tight">
              <span className="text-sm font-bold text-white tracking-wide" style={{fontFamily: 'Manrope'}}>ANNE FLINN ADVERTISING</span>
              <span className="text-xs font-medium text-[#B7EB1B]">Advertise Smarter with Us</span>
            </div>
          </a>
          <div className="flex items-center gap-5">
            <a href="#tools" className="hidden sm:inline text-sm text-white/70 hover:text-white">Tools</a>
            <a href="#about" className="hidden sm:inline text-sm text-white/70 hover:text-white">About Anne</a>
            {token && (
              <Button
                data-testid="suite-nav-dashboard"
                onClick={() => navigate("/dashboard")}
                variant="outline"
                className="rounded-full font-semibold border-[#B7EB1B]/40 bg-[#B7EB1B]/10 hover:bg-[#B7EB1B]/20 text-[#B7EB1B]"
              >
                <LayoutDashboard className="w-4 h-4 mr-1.5" />
                My Email Dashboard
              </Button>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#B7EB1B]/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[#119AB3]/20 rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-5xl mx-auto px-6 py-10 lg:py-14 text-center">
          <Badge className="bg-white/10 text-white border-white/20 mb-4 px-4 py-1.5">
            <Sparkles className="w-3 h-3 mr-1.5 inline text-[#B7EB1B]" />
            A growing suite of marketing tools built for small business
          </Badge>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-4" style={{fontFamily: 'Manrope'}}>
            We've spent <span className="text-[#B7EB1B]">30 years</span> helping businesses grow.
            <span className="text-white/80"> Now we're packaging that know-how into simple tools you can use yourself.</span>
          </h1>
          <p className="text-base sm:text-lg text-white/70 max-w-2xl mx-auto leading-relaxed mb-2">
            I'm a small business owner too — I built these tools to alleviate the stress of design, email, blogs, and everything else marketing throws at you.
          </p>
          <p className="text-lg sm:text-xl text-white font-bold italic" style={{fontFamily: 'Manrope'}}>
            My tools do less. On purpose. ~Anne Flinn
          </p>
        </div>
      </section>

      {/* Tools Grid */}
      <section id="tools" className="relative pb-20 lg:pb-28 -mt-4">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-8">
            <p className="text-sm uppercase tracking-widest text-[#B7EB1B] font-semibold mb-2">The Suite</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold mb-2" style={{fontFamily: 'Manrope'}}>
              Pick the tools you need. Ignore the rest.
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto text-base">
              Each one is independent. Each one was built to simplify a part of your week.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {TOOLS.map((tool) => {
              const Icon = tool.icon;
              return (
                <Card
                  key={tool.key}
                  data-testid={`suite-tool-card-${tool.key}`}
                  className="h-full bg-white/5 backdrop-blur border-white/10 rounded-3xl overflow-hidden hover:bg-white/10 transition-all hover:scale-[1.01] flex flex-col"
                >
                  <CardContent className="p-0 flex-1 flex flex-col">
                    <div className={`bg-gradient-to-br ${tool.color} p-6 relative`}>
                      <div className="flex items-start justify-between">
                        <div className="w-14 h-14 bg-white/10 backdrop-blur rounded-2xl flex items-center justify-center">
                          <Icon className="w-7 h-7 text-[#B7EB1B]" />
                        </div>
                        <Badge className={`${STATUS_STYLES[tool.status]} font-semibold px-3 py-1 rounded-full`}>
                          {tool.statusLabel}
                        </Badge>
                      </div>
                      <h3 className="text-2xl font-bold mt-5 text-white" style={{fontFamily: 'Manrope'}}>{tool.name}</h3>
                      <p className="text-[#B7EB1B] text-sm mt-1 font-medium">{tool.tagline}</p>
                    </div>

                    <div className="p-6 space-y-4 flex-1 flex flex-col">
                      <p className="text-white/80 text-sm leading-relaxed">{tool.description}</p>

                      <div className="grid grid-cols-2 gap-2">
                        {tool.features.map((f) => (
                          <div key={f} className="flex items-center gap-2 text-xs text-white/60">
                            <CheckCircle2 className="w-3 h-3 text-[#B7EB1B] flex-shrink-0" />
                            <span>{f}</span>
                          </div>
                        ))}
                      </div>

                      {tool.note && (
                        <div
                          data-testid={`suite-tool-note-${tool.key}`}
                          className="mt-2 flex items-start gap-2 text-xs text-white/55 bg-white/5 border border-white/10 rounded-lg px-3 py-2"
                        >
                          <Info className="w-3.5 h-3.5 text-[#B7EB1B]/80 flex-shrink-0 mt-0.5" />
                          <span>{tool.note}</span>
                        </div>
                      )}

                      <div className="text-xs text-white/40 italic border-t border-white/10 pt-3 mt-auto">
                        Replaces: {tool.replaces}
                      </div>

                      <div className="flex items-center justify-between pt-2">
                        <div className="text-2xl font-bold text-white" style={{fontFamily: 'Manrope'}}>
                          {tool.price}
                        </div>
                        {tool.status === "live" && tool.external && (
                          <div className="flex items-center gap-2">
                            {tool.key === "blog" && (
                              <Button
                                data-testid={`suite-tool-demo-${tool.key}`}
                                variant="outline"
                                disabled
                                title="Free demo for Simply Smarter Blog Posts is coming soon"
                                className="rounded-full font-semibold border-slate-500/30 bg-slate-500/10 text-slate-400 cursor-not-allowed opacity-70"
                              >
                                <Clock className="w-3 h-3 mr-1.5" /> Free Demo · Soon
                              </Button>
                            )}
                            <a href={tool.href} target="_blank" rel="noopener noreferrer">
                              <Button
                                data-testid={`suite-tool-cta-${tool.key}`}
                                className="bg-[#B7EB1B] hover:bg-[#B7EB1B]/90 text-[#0a1e3a] rounded-full font-semibold"
                              >
                                {tool.cta} <ArrowRight className="w-4 h-4 ml-1.5" />
                              </Button>
                            </a>
                          </div>
                        )}
                        {tool.status === "live" && !tool.external && (
                          <div className="flex items-center gap-2">
                            <Button
                              data-testid={`suite-tool-cta-${tool.key}`}
                              onClick={() => navigate(tool.href)}
                              className="bg-[#B7EB1B] hover:bg-[#B7EB1B]/90 text-[#0a1e3a] rounded-full font-semibold"
                            >
                              {tool.cta} <ArrowRight className="w-4 h-4 ml-1.5" />
                            </Button>
                          </div>
                        )}
                        {tool.status !== "live" && (
                          <Button
                            data-testid={`suite-tool-cta-${tool.key}`}
                            onClick={() => {
                              toggleInterest(tool.key);
                              document.getElementById("waitlist")?.scrollIntoView({behavior: "smooth"});
                            }}
                            variant="outline"
                            className="rounded-full font-semibold border-white/20 bg-white/5 hover:bg-white/10 text-white"
                          >
                            <Clock className="w-4 h-4 mr-1.5" /> {tool.cta}
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Anne */}
      <section id="about" className="relative py-20 lg:py-28 border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-2 relative">
              <div className="aspect-square rounded-3xl overflow-hidden border-4 border-[#B7EB1B]/20 shadow-2xl">
                <img
                  src="/brand/anne-headshot.jpg"
                  alt="Anne Flinn — Founder, Anne Flinn Advertising"
                  className="w-full h-full object-cover"
                  data-testid="anne-headshot"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-[#B7EB1B] rounded-2xl p-5 shadow-2xl rotate-3 hidden sm:block">
                <p className="text-[#0a1e3a] font-bold text-sm leading-tight" style={{fontFamily: 'Manrope'}}>
                  20+ years.<br />
                  Real businesses.<br />
                  Real results.
                </p>
              </div>
            </div>

            <div className="lg:col-span-3">
              <p className="text-sm uppercase tracking-widest text-[#B7EB1B] font-semibold mb-3">Why I Built This</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-6" style={{fontFamily: 'Manrope'}}>
                Meet Anne.
              </h2>
              <div className="space-y-4 text-white/80 text-lg leading-relaxed">
                <p>
                  I'm an entrepreneur who transforms challenges into opportunities. For over two decades,
                  I built <strong className="text-white">AFP Photography, LLC</strong>, using my photography to become
                  the <strong className="text-white">Cayman Islands' largest postcard distributor</strong>.
                </p>
                <p>
                  I honed my marketing skills at Downtown Athletic Store and Energizer, later excelling in
                  radio advertising sales and as <strong className="text-white">Director of Digital Sales in Charlottesville</strong>.
                </p>
                <p>
                  When the <strong className="text-white">2020 pandemic</strong> hit tourism and radio,
                  I launched <strong className="text-white">Anne Flinn Advertising, LLC</strong> — helping
                  businesses thrive with innovative, results-driven strategies.
                </p>
                <p className="border-l-4 border-[#B7EB1B] pl-6 italic text-white/90">
                  Resilience and creative drive make me an essential ally for businesses ready to succeed.
                  Now I'm packaging the same expertise into simple tools you can use yourself — without
                  needing to hire me.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <Badge className="bg-white/10 text-white border-white/20 px-3 py-1.5">
                  <Heart className="w-3 h-3 mr-1.5 text-[#B7EB1B]" /> Built in Maine
                </Badge>
                <Badge className="bg-white/10 text-white border-white/20 px-3 py-1.5">
                  20+ years of agency experience
                </Badge>
                <Badge className="bg-white/10 text-white border-white/20 px-3 py-1.5">
                  Empathy-led, no-overwhelm design
                </Badge>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote / Philosophy strip */}
      <section className="relative py-16 bg-gradient-to-r from-[#0a1e3a] via-[#0d9488] to-[#0a1e3a]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Quote className="w-10 h-10 text-[#B7EB1B] mx-auto mb-4" />
          <p className="text-2xl sm:text-3xl font-bold text-white leading-relaxed" style={{fontFamily: 'Manrope'}}>
            "My tools do less. <span className="text-[#B7EB1B]">On purpose.</span>"
          </p>
          <p className="text-white/60 mt-4">
            Only the essentials. Beautifully designed. Genuinely simple.
          </p>
        </div>
      </section>

      {/* Waitlist / Get Notified */}
      <section id="waitlist" className="relative py-20 lg:py-24">
        <div className="max-w-3xl mx-auto px-6">
          <Card className="bg-white/5 backdrop-blur border-white/10 rounded-3xl">
            <CardContent className="p-8 sm:p-12">
              {!submitted ? (
                <>
                  <div className="text-center mb-8">
                    <Badge className="bg-[#B7EB1B] text-[#0a1e3a] mb-4 px-3 py-1 font-semibold">
                      Be the first to know
                    </Badge>
                    <h2 className="text-3xl sm:text-4xl font-bold mb-3" style={{fontFamily: 'Manrope'}}>
                      Want early access?
                    </h2>
                    <p className="text-white/70">
                      Drop your email — I'll let you know when more tools go live, and Founding Members get
                      <span className="text-[#B7EB1B] font-semibold"> locked-in lifetime pricing</span>.
                    </p>
                  </div>

                  <form onSubmit={handleWaitlist} className="space-y-4">
                    <input
                      data-testid="waitlist-email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@yourbusiness.com"
                      className="w-full h-14 px-5 rounded-2xl bg-white/10 border border-white/20 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#B7EB1B]"
                    />

                    {interest.length > 0 && (
                      <div className="text-sm text-white/60">
                        Interested in: {interest.map((k) => TOOLS.find((t) => t.key === k)?.name).join(", ")}
                      </div>
                    )}

                    <Button
                      data-testid="waitlist-submit"
                      type="submit"
                      className="w-full h-14 bg-[#B7EB1B] hover:bg-[#B7EB1B]/90 text-[#0a1e3a] rounded-2xl font-bold text-base"
                    >
                      Join the List <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>

                    <p className="text-xs text-white/40 text-center">
                      No spam. Just product updates when a new tool launches.
                    </p>
                  </form>
                </>
              ) : (
                <div className="text-center py-6" data-testid="waitlist-success">
                  <div className="w-16 h-16 bg-[#B7EB1B]/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-10 h-10 text-[#B7EB1B]" />
                  </div>
                  <h3 className="text-2xl font-bold mb-2" style={{fontFamily: 'Manrope'}}>You're on the list! 🎉</h3>
                  <p className="text-white/70">
                    I'll reach out the moment your tools are ready.
                    <br />
                    Meanwhile, why not try <a href="/login" className="text-[#B7EB1B] underline">Simply Smarter Email</a> — it's live now.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/brand/afa-logo-pin.png" alt="Anne Flinn Advertising" className="w-8 h-8" />
            <div className="text-sm text-white/60">
              © {new Date().getFullYear()} Anne Flinn Advertising, LLC · <span className="text-[#B7EB1B]">Advertise Smarter with Us</span>
            </div>
          </div>
          <div className="text-xs text-white/40">
            Email infrastructure powered by Swiftlett
          </div>
        </div>
      </footer>
    </div>
  );
}
